<template>
  <div id="hs-analytics-modal-parent">
    <Page title="HS分析">
      <template v-slot:action>
        <ActionButton title="HS分析とは" color="primary" shape="round" width="150" @click="showTutorial" />
      </template>
      <template v-slot:content>
        <div class="uk-card uk-card-default uk-card-body" :class="{ 'main-hidden': activePath === 'sub' }">
          <div class="content-header">
            <ActionButton
              title="分析対象追加"
              color="primary"
              width="150"
              @click="onAddButtonClick"
              :uk-tooltip="`title: 分析可能人数は${participantsMin}人〜${participantsMax}人です。; pos: top-left;`"
            />
            <div class="right-contents">
              <ActionButton title="印刷" color="primary" shape="round" width="150" @click="onPrintButtonClick" />
              <ActionButton
                title="CSV出力"
                color="primary"
                shape="round"
                width="150"
                @click="onCsvButtonClick"
                :uk-tooltip="`title: CSVに退職者は含まれません。; pos: top-left;`"
              />
            </div>
          </div>
          <div class="content-main">
            <div class="selected-users">
              <UserSelector :users="selectedUsers" :readOnly="true" :selectable="true" @user-click="onUserSelect" />
            </div>
            <div class="chart">
              <HumanSkillChart :chartData="_chartData.HumanSkill"/>
            </div>
          </div>
        </div>
        <div
          class="uk-flex-top department-user-selector-form-modal"
          uk-modal
          ref="selectionModal"
          container="#hs-analytics-modal-parent"
          :bg-close="false"
        >
          <div class="uk-modal-dialog uk-margin-auto-vertical">
            <div class="uk-modal-body">
              <DepartmentUserSelector
                :key="departmentUserSelectorFormKey"
                :full="true"
                :analysisFlg="true"
                :includeRetired="true"
                :initialSelectedUsers="selectedUsers"
                :participantsMax="participantsMax"
                :participantsMin="participantsMin"
                @user-selection-change="onTargetUserSelectionChange"
                @on-selection-done-button-click="onSelectionDoneButtonClick"
                @close="onCloseButtonClick"
                v-on:keydown.esc="onCloseButtonClick"
              />
            </div>
          </div>
        </div>
      </template>
    </Page>
  </div>
</template>
<style lang="scss" scoped>
.action .action-button:first-child {
  margin-right: $spacing-medium;
}
.uk-card-body {
  .content-main {
    display: flex;
  }
  .content-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: $spacing-ex-small;
  }
}
.chart {
  flex: 1;
  position: relative;
}
.selected-users {
  width: 250px;
  margin-right: $spacing-medium;
  max-height: 500px;
  min-height: 300px;
  .user-selector-container {
    height: 100%;
  }
}
.uk-modal-dialog {
  width: unset;
  min-width: 960px;
}
.main-hidden {
  display: none;
}
.right-contents {
  margin: 0 0 0 auto;
  & > button:first-child {
    margin-right: 6px;
  }
}
</style>
<script lang="ts">
import { Options } from "vue-class-component";
import Page from "../molecules/Page.vue";
import ActionButton from "../atoms/ActionButton.vue";
import ActionMenu from "../molecules/ActionMenu.vue";
import UserSelector from "../molecules/UserSelector.vue";
import HumanSkillChart from "../atoms/HumanSkillChart.vue";
import DepartmentUserSelector from "../organisms/DepartmentUserSelector.vue";
import { SelectableProfile, UserRDStats } from "../../types";
import { StatsItemEnum, SystemApi } from "../../autogen";
import { print } from "../../helpers/view-utils";
import ChartViewMixin from "../../helpers/mixins/chart-view";
import Store from "../../store";
import { InstructionLevel } from "../../types";
import { StatsApi, GetSummaryResponse } from "../../autogen";

@Options({
  components: {
    Page,
    ActionButton,
    UserSelector,
    DepartmentUserSelector,
    ActionMenu,
    HumanSkillChart
  }
})
export default class HSAnalytics extends ChartViewMixin {
  selectedUsers: SelectableProfile[] = [];
  stats: UserRDStats[] = [];
  activePath = "main";
  participantsMin = 1;
  participantsMax = 100;
  focusedUser: Partial<SelectableProfile> = {};
  departmentUserSelectorFormKey = 0;
  loadingFunction = true;
  tutorialImagePath = require("@/assets/image/HSTutorial.png");

  private readonly storeInstance = Store.getInstance();

  _chartData?: GetSummaryResponse = {
    PersonalityMatrix: [],
    PMAnalytics: [],
    RDAnalytics: [],
    PersonalityActivityMotivation: [],
    HumanSkill: [],
    CulturalFit: []
  };
  departmentId?: string;
  notFound = false;
  skill?: any;

  targetStats = StatsItemEnum.HumanSkill;
  async onSelectionDone() {
    this.stats = this.selectedUsers.map(selectedUser => ({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      axis: this.chartData![selectedUser.id].stats[StatsItemEnum.RdAnalytics]!,
      id: selectedUser.id,
      name: `${selectedUser.lastName} ${selectedUser.firstName}`,
      department: selectedUser.department
    }));
    const departmentId = "0";
    this.departmentId = departmentId;
    try {
      const response = await new StatsApi().getDepartmentSummary({
        departmentId
      });
      this._chartData = response.data;

      // レーダーチャート各項目のインデックスを定義
      const INDEX_COMM_TSUTAERU = 0;
      const INDEX_COMM_UKEIRERU = 1;
      const INDEX_COMM_TYOUSEI = 2;
      const INDEX_LEADER_PERFORMANCE = 3;
      const INDEX_LEADER_MAINTENANCE = 4;
      const INDEX_STRESS_CONTROL = 5;
      const INDEX_GYOUMU_SUIKOU = 6;
      const INDEX_RINKI_OUHEN = 7;
      const INDEX_SOUZOU_SOUI_KUFUU = 8;

      // レーダーチャートの各項目の値格納用変数を定義
      let commTsutaeru = 0.0; // コミュニケーション力 伝える力
      let commUkeireru = 0.0; // コミュニケーション力 受け入れる力
      let commTyousei = 0.0; // コミュニケーション力 調整する力
      let leaderPerformance = 0.0; // リーダーシップ力 パフォーマンス
      let leaderMaintenance = 0.0; // リーダーシップ力 メンテナンス
      let stressControl = 0.0; // ストレスコントロール力
      let gyoumuSuikou = 0.0; // 業務遂行力
      let rinkiOuhen = 0.0; // 臨機応変さ
      let souzouSouiKufuu = 0.0; // 創造・創意工夫力
      let people = 0;

      // 選択した従業員のヒューマンスキルを取得
      // chartDataに選択した従業員の一覧があるため
      // その中から従業員別のヒューマンスキル各値をhumanSkillDataListに格納する
      for (let item in this.chartData) {
        let dataList = this.chartData[item];
        let humanSkillDataList = dataList.stats.HumanSkill;
        for (let humanSkillData in humanSkillDataList) {
          switch(Number(humanSkillData)) {
            case INDEX_COMM_TSUTAERU:
              commTsutaeru = commTsutaeru + humanSkillDataList[Number(humanSkillData)].value;
              break;
            case INDEX_COMM_UKEIRERU:
              commUkeireru = commUkeireru + humanSkillDataList[Number(humanSkillData)].value;
              break;
            case INDEX_COMM_TYOUSEI:
              commTyousei = commTyousei + humanSkillDataList[Number(humanSkillData)].value;
              break;
            case INDEX_LEADER_PERFORMANCE:
              leaderPerformance = leaderPerformance + humanSkillDataList[Number(humanSkillData)].value;
              break;
            case INDEX_LEADER_MAINTENANCE:
              leaderMaintenance = leaderMaintenance + humanSkillDataList[Number(humanSkillData)].value;
              break;
            case INDEX_STRESS_CONTROL:
              stressControl = stressControl + humanSkillDataList[Number(humanSkillData)].value;
              break;
            case INDEX_GYOUMU_SUIKOU:
              gyoumuSuikou = gyoumuSuikou + humanSkillDataList[Number(humanSkillData)].value;
              break;
            case INDEX_RINKI_OUHEN:
              rinkiOuhen = rinkiOuhen + humanSkillDataList[Number(humanSkillData)].value;
              break;
            case INDEX_SOUZOU_SOUI_KUFUU:
              souzouSouiKufuu = souzouSouiKufuu + humanSkillDataList[Number(humanSkillData)].value;
              break;
          }
        }
        people++;
      }

      // 選択された従業員の各項目の平均値をレーダーチャートにセット
      this.skill = response.data.HumanSkill;
      this.skill[INDEX_COMM_TSUTAERU].value = commTsutaeru / people; // コミュニケーション力 伝える力
      this.skill[INDEX_COMM_UKEIRERU].value = commUkeireru / people; // コミュニケーション力 受け入れる力
      this.skill[INDEX_COMM_TYOUSEI].value = commTyousei / people; // コミュニケーション力 調整する力
      this.skill[INDEX_LEADER_PERFORMANCE].value = leaderPerformance / people; // リーダーシップ力 パフォーマンス
      this.skill[INDEX_LEADER_MAINTENANCE].value = leaderMaintenance / people; // リーダーシップ力 メンテナンス
      this.skill[INDEX_STRESS_CONTROL].value = stressControl / people; // ストレスコントロール力
      this.skill[INDEX_GYOUMU_SUIKOU].value = gyoumuSuikou / people; // 業務遂行力
      this.skill[INDEX_RINKI_OUHEN].value = rinkiOuhen / people; // 臨機応変さ
      this.skill[INDEX_SOUZOU_SOUI_KUFUU].value = souzouSouiKufuu / people; // 創造・創意工夫力
      this._chartData.HumanSkill = this.skill;

    } catch (err) {
      this.notFound = true;
      if (err.response.status !== 404) {
        Store.getInstance().setInstructionContent(InstructionLevel.Alert, "エラーが発生しました");
      }
    }
  }
  onUserSelect(selectedUser: SelectableProfile) {
    this.focusedUser = selectedUser;
  }
  onPrintButtonClick() {
    if (!this.selectedUsers.length) {
      alert("分析対象を1人以上追加してください");
      return;
    }
    if (this._chartData !== undefined) {
      print(StatsItemEnum.HumanSkill, this._chartData.HumanSkill);
    }
  }
  async onCsvButtonClick() {
    if (!this.selectedUsers.length) {
      alert("出力対象を1人以上追加してください");
      return;
    } else {
      this.downLoadCsvFile(this.chartData);
    }
  }
  private async downLoadCsvFile(outputDataList: any) {
    Store.getInstance().setIsLoading(true);
    const encoding = require("encoding-japanese");
    try {
      let headerData = ",";
      headerData = headerData + "コミュニケーション力 伝える力,";
      headerData = headerData + "コミュニケーション力 受け入れる力,";
      headerData = headerData + "コミュニケーション力 調整する力,";
      headerData = headerData + "リーダーシップ力 パフォーマンス,";
      headerData = headerData + "リーダーシップ力 メンテナンス,";
      headerData = headerData + "ストレスコントロール力,";
      headerData = headerData + "業務遂行力,";
      headerData = headerData + "臨機応変さ,";
      headerData = headerData + "創造・創意工夫力\n";

      let rowData = "";
      // レーダーチャートの各項目の値格納用変数を定義
      let commTsutaeru = 0.0; // コミュニケーション力 伝える力
      let commUkeireru = 0.0; // コミュニケーション力 受け入れる力
      let commTyousei = 0.0; // コミュニケーション力 調整する力
      let leaderPerformance = 0.0; // リーダーシップ力 パフォーマンス
      let leaderMaintenance = 0.0; // リーダーシップ力 メンテナンス
      let stressControl = 0.0; // ストレスコントロール力
      let gyoumuSuikou = 0.0; // 業務遂行力
      let rinkiOuhen = 0.0; // 臨機応変さ
      let souzouSouiKufuu = 0.0; // 創造・創意工夫力
      let people = 0;
      // レーダーチャート各項目のインデックスを定義
      const INDEX_COMM_TSUTAERU = 0;
      const INDEX_COMM_UKEIRERU = 1;
      const INDEX_COMM_TYOUSEI = 2;
      const INDEX_LEADER_PERFORMANCE = 3;
      const INDEX_LEADER_MAINTENANCE = 4;
      const INDEX_STRESS_CONTROL = 5;
      const INDEX_GYOUMU_SUIKOU = 6;
      const INDEX_RINKI_OUHEN = 7;
      const INDEX_SOUZOU_SOUI_KUFUU = 8;
      for (let item in outputDataList) {
        // 氏名を出力
        let dataList = outputDataList[item];
        rowData = rowData + dataList.profile.lastName + " " + dataList.profile.firstName + ",";
        people++;
        // 各ヒューマンスキルデータを出力
        let humanSkillDataList = dataList.stats.HumanSkill;
        for (let humanSkillData in humanSkillDataList) {
          rowData = rowData + Math.round(humanSkillDataList[Number(humanSkillData)].value);
          if (Number(humanSkillData) != 8) {
            rowData = rowData + ",";
          } else {
            rowData = rowData + "\n";
          }
          // 選択した従業員の各ヒューマンスキルの合計値を取得
          switch(Number(humanSkillData)) {
            case INDEX_COMM_TSUTAERU:
              commTsutaeru = commTsutaeru + humanSkillDataList[Number(humanSkillData)].value;
              break;
            case INDEX_COMM_UKEIRERU:
              commUkeireru = commUkeireru + humanSkillDataList[Number(humanSkillData)].value;
              break;
            case INDEX_COMM_TYOUSEI:
              commTyousei = commTyousei + humanSkillDataList[Number(humanSkillData)].value;
              break;
            case INDEX_LEADER_PERFORMANCE:
              leaderPerformance = leaderPerformance + humanSkillDataList[Number(humanSkillData)].value;
              break;
            case INDEX_LEADER_MAINTENANCE:
              leaderMaintenance = leaderMaintenance + humanSkillDataList[Number(humanSkillData)].value;
              break;
            case INDEX_STRESS_CONTROL:
              stressControl = stressControl + humanSkillDataList[Number(humanSkillData)].value;
              break;
            case INDEX_GYOUMU_SUIKOU:
              gyoumuSuikou = gyoumuSuikou + humanSkillDataList[Number(humanSkillData)].value;
              break;
            case INDEX_RINKI_OUHEN:
              rinkiOuhen = rinkiOuhen + humanSkillDataList[Number(humanSkillData)].value;
              break;
            case INDEX_SOUZOU_SOUI_KUFUU:
              souzouSouiKufuu = souzouSouiKufuu + humanSkillDataList[Number(humanSkillData)].value;
              break;
          }
        }
      }
      // 平均値を計算して出力
      rowData = rowData + "平均,";
      rowData = rowData + Math.round(commTsutaeru / people) + ",";
      rowData = rowData + Math.round(commUkeireru / people) + ",";
      rowData = rowData + Math.round(commTyousei / people) + ",";
      rowData = rowData + Math.round(leaderPerformance / people) + ",";
      rowData = rowData + Math.round(leaderMaintenance / people) + ",";
      rowData = rowData + Math.round(stressControl / people) + ",";
      rowData = rowData + Math.round(gyoumuSuikou / people) + ",";
      rowData = rowData + Math.round(rinkiOuhen / people) + ",";
      rowData = rowData + Math.round(souzouSouiKufuu / people);

      const bom = new Uint8Array([0xef, 0xbb, 0xbf]);

      const url = window.URL.createObjectURL(new Blob([bom, headerData + rowData], { type: "text/csv;charset=Shift_JIS" }));
      const anchor = document.createElement("a");
      document.body.appendChild(anchor);
      anchor.setAttribute("href", url);
      anchor.setAttribute("download", `HS分析_${new Date().toISOString()}.csv`);
      anchor.click();
      anchor.remove();
      document.body.onfocus = null;
    } catch (err) {
      let errMessage = "エラーが発生しました。時間を置いて再度試すか、窓口までお問い合わせください。";
      if (err.response && err.response.data) {
        errMessage = err.response.data.detail;
        console.log(errMessage);
      }
      Store.getInstance().setInstructionContent(InstructionLevel.Alert, errMessage);
    } finally {
      Store.getInstance().setIsLoading(false);
    }
  }
}
</script>
